import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { BrickSettingsService, InnerWallBrickSettingRemoteDto } from '../../../../../../../generated/swagger';
import { LayoutBrickAddedToWall, RemoveBrickFromWall } from '../../../brick-instances/brick-instances.actions';
import { BrickInstancesSelectors } from '../../../brick-instances/brick-instances.selectors';
import { IBrickDescription } from '../../../brick-instances/brick-instances.state';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import {
   ChangeBrickDefinitionMenuOpeningStateForInnerWallBrick,
   RemoveBrickFromInnerWallBrick,
   ToggleBrickDefinitionMenuOpeningStateForInnerWallBrick,
} from './inner-wall-brick-settings.actions';

export interface InnerWallBrickSettingsStateModel extends IBrickSettingStateModel<InnerWallBrickSettingRemoteDto> {
   isBrickDefinitionMenuOpen: boolean;
}

function getDefaultSpecificBrickSetting(): InnerWallBrickSettingRemoteDto {
   return {
      commonBrickSettingRemoteDto: null,
      spacingType: 'todo',
      wallItems: [],
   };
}

@State<InnerWallBrickSettingsStateModel>({
   name: 'innerWallBrickSettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
      isBrickDefinitionMenuOpen: false,
   },
})
@Injectable()
export class InnerWallBrickSettingsState extends SpecificBrickSettingsStateBase<InnerWallBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([InnerWallBrickSettingsState])
   public static getIsBrickDefinitionMenuOpen(state: InnerWallBrickSettingsStateModel): boolean {
      return state.isBrickDefinitionMenuOpen;
   }

   @Selector([InnerWallBrickSettingsState, InnerWallBrickSettingsState.getSettingModelFn])
   public static brickInstanceIdsForInnerWallBrickFn(
      state: InnerWallBrickSettingsStateModel,
      getSettingModelFn: (state, brickInstanceId) => InnerWallBrickSettingRemoteDto
   ): (brickId: string) => string[] {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).wallItems.map((x) => x.brickInstanceId);
      };
   }

   @Selector([InnerWallBrickSettingsState])
   private static getBrickSettings(state: InnerWallBrickSettingsStateModel) {
      if (_.isNil(state.specificBrickSettings.brickSettingsEditMode)) {
         return state.specificBrickSettings.brickSettings;
      } else {
         return state.specificBrickSettings.brickSettingsEditMode;
      }
   }

   @Selector([InnerWallBrickSettingsState.getBrickSettings])
   public static isBrickInstancePartOfInnerWallBrickFn(settings: { [p: string]: InnerWallBrickSettingRemoteDto }): (brickInstanceId: string) => boolean {
      return (brickInstanceId: string) => {
         return _(settings)
            .values()
            .some(
               (x: InnerWallBrickSettingRemoteDto) =>
                  !_.isNil(x) && !_.isNil(x.wallItems) && x.wallItems.some((innerBrick) => innerBrick.brickInstanceId === brickInstanceId)
            );
      };
   }

   @Selector([InnerWallBrickSettingsState.getBrickSettings])
   public static getBrickInstanceIdOfParentFn(settings: { [p: string]: InnerWallBrickSettingRemoteDto }): (brickInstanceId: string) => string {
      return (brickInstanceId: string) => {
         return _.findKey(settings, (x: InnerWallBrickSettingRemoteDto) => {
            return !_.isNil(x) && !_.isNil(x.wallItems) && x.wallItems.some((innerBrick) => innerBrick.brickInstanceId === brickInstanceId);
         });
      };
   }

   @Selector([InnerWallBrickSettingsState.getBrickSettings])
   public static isBrickInstanceHostingChildBrick(settings: {
      [p: string]: InnerWallBrickSettingRemoteDto;
   }): (hostingBrickId: string, targetBrickId: string) => boolean {
      return (hostingBrickId: string, targetBrickId: string, deepCheckFn: (hostingBrickId: string, targetBrickId: string, deepCheck) => boolean = null) => {
         if (!_.has(settings, hostingBrickId)) {
            return false;
         }
         const isTargetBrickPresent = settings[hostingBrickId].wallItems.some((x) => x.brickInstanceId === targetBrickId);
         if (isTargetBrickPresent || _.isNil(deepCheckFn)) {
            return isTargetBrickPresent;
         }
         return settings[hostingBrickId].wallItems.some((x) => deepCheckFn(x.brickInstanceId, targetBrickId, true));
      };
   }

   @Action(ToggleBrickDefinitionMenuOpeningStateForInnerWallBrick)
   public toggleBrickDefinitionMenuOpeningState(
      ctx: StateContext<InnerWallBrickSettingsStateModel>,
      action: ToggleBrickDefinitionMenuOpeningStateForInnerWallBrick
   ) {
      ctx.patchState({
         isBrickDefinitionMenuOpen: !ctx.getState().isBrickDefinitionMenuOpen,
      });
   }

   @Action(ChangeBrickDefinitionMenuOpeningStateForInnerWallBrick)
   public changeBrickDefinitionMenuOpeningState(
      ctx: StateContext<InnerWallBrickSettingsStateModel>,
      action: ChangeBrickDefinitionMenuOpeningStateForInnerWallBrick
   ) {
      ctx.patchState({
         isBrickDefinitionMenuOpen: action.isOpen,
      });
   }

   @Action(RemoveBrickFromInnerWallBrick)
   @ImmutableContext()
   public removeBrickFromInnerWallBrick(ctx: StateContext<InnerWallBrickSettingsStateModel>, action: RemoveBrickFromInnerWallBrick) {
      ctx.setState((state: InnerWallBrickSettingsStateModel) => {
         const brickSettingModel: InnerWallBrickSettingRemoteDto = InnerWallBrickSettingsState.getSettingModelOrDefault(state, action.brickWallInstanceId);

         _.remove(brickSettingModel.wallItems, (x) => x.brickInstanceId === action.brickInstanceIdToRemove);
         state.specificBrickSettings.brickSettingsEditMode[action.brickWallInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(RemoveBrickFromWall)
   public removeBrickFromWall(ctx: StateContext<InnerWallBrickSettingsStateModel>, action: RemoveBrickFromWall) {
      const settings = this.store.selectSnapshot(InnerWallBrickSettingsState.getBrickSettings);

      const brickHost = _(settings)
         .pickBy(
            (value, key) =>
               !_.isNil(value) && !_.isNil(value.wallItems) && value.wallItems.some((innerBrick) => innerBrick.brickInstanceId === action.brickInstanceId)
         )
         .keys()
         .head();
      if (_.isEmpty(brickHost)) {
         return;
      }
      return this.store.dispatch(new RemoveBrickFromInnerWallBrick(brickHost, action.brickInstanceId));
   }

   @Action(LayoutBrickAddedToWall)
   @ImmutableContext()
   // this adds a new item to the wall!
   public handleLayoutBrickAddedToWall(ctx: StateContext<InnerWallBrickSettingsStateModel>, action: LayoutBrickAddedToWall) {
      if (
         this.store.selectSnapshot(BrickInstancesSelectors.brickDescriptionFn)(action.responsibleLayoutBrickInstanceId).definition.brickDefinitionId !==
         'innerWall'
      ) {
         return;
      }
      ctx.setState((state: InnerWallBrickSettingsStateModel) => {
         const brickSettingModel: InnerWallBrickSettingRemoteDto = InnerWallBrickSettingsState.getSettingModelOrDefault(
            state,
            action.responsibleLayoutBrickInstanceId
         );
         const brickDescription: IBrickDescription = this.store.selectSnapshot(BrickInstancesSelectors.brickDescriptionFn)(action.newBrickInstanceId);

         brickSettingModel.wallItems.unshift({
            brickInstanceId: brickDescription.brickInstance.brickInstanceId,
         });
         state.specificBrickSettings.brickSettingsEditMode[action.responsibleLayoutBrickInstanceId] = brickSettingModel;
         state.isBrickDefinitionMenuOpen = false;
         return state;
      });
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: InnerWallBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeInnerWallBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
